import React, { useState, useCallback } from 'react';
import { ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
} from '../ui/sheet';
import { Separator } from '../ui/separator';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';
import { Spinner } from '../ui/spinner';
import { useTrans } from '../../hooks/useTrans';
import { trackAndTraceUrl } from '../../lib/routes';
import { GoodTemperatureActions } from './GoodTemperatureActions';

export function useOrderDrawer() {
    const translate = useTrans();
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [orderDetails, setOrderDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState(null);

    const openDrawer = useCallback((orderId) => {
        if (!orderId) return;

        setSelectedOrderId(orderId);
        setLoadingDetails(true);
        setOrderDetails(null);
        setDetailsError(null);

        fetch(`/ajax/orders/${orderId}/details`, {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        })
            .then((res) => {
                if (!res.ok) throw new Error(res.statusText);
                return res.json();
            })
            .then((data) => setOrderDetails(data))
            .catch(() => {
                setOrderDetails(null);
                setDetailsError(translate('Er is een fout opgetreden bij het ophalen van deze order. Probeer het later opnieuw.'));
            })
            .finally(() => setLoadingDetails(false));
    }, []);

    function closeDrawer() {
        setSelectedOrderId(null);
        setOrderDetails(null);
        setDetailsError(null);
    }

    return { selectedOrderId, orderDetails, loadingDetails, detailsError, openDrawer, closeDrawer };
}

export function OrderDetailDrawer({ selectedOrderId, orderDetails, loadingDetails, detailsError, closeDrawer }) {
    const translate = useTrans();

    return (
        <Sheet open={!!selectedOrderId} onOpenChange={(open) => { if (!open) closeDrawer(); }}>
            <SheetContent className="w-full sm:max-w-md">
                <SheetHeader className="pb-0">
                    <SheetTitle className="text-2xl font-bold text-[#003545]">
                        {translate('Orderdetails')}
                    </SheetTitle>
                    <SheetDescription className="sr-only">
                        {translate('Orderdetails')}
                    </SheetDescription>
                    <Separator />
                </SheetHeader>
                <div className="flex-1 overflow-y-auto flex flex-col">
                {selectedOrderId && (<>
                    {loadingDetails && (
                        <div className="flex-1 flex flex-col items-center justify-center gap-3">
                            <Spinner className="size-8 text-[#003545]" />
                            <span className="text-sm text-gray-500">{translate('Orderdetails ophalen...')}</span>
                        </div>
                    )}
                    {detailsError && (
                        <div className="flex-1 flex items-center justify-center px-4">
                            <p className="text-sm text-red-600 text-center">{detailsError}</p>
                        </div>
                    )}
                    {orderDetails && (<>
                        <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 px-4 py-4">
                            <span className="text-sm font-bold text-[#003545]">{translate('Orderdatum')}</span>
                            <span className="text-sm">{orderDetails.order.date || '-'}</span>
                            <span className="text-sm font-bold text-[#003545]">{translate('Ordernummer')}</span>
                            <span className="text-sm">{orderDetails.order.externalOrderId || '-'}</span>
                            <span className="text-sm font-bold text-[#003545]">{translate('Status')}</span>
                            <span className="text-sm">{orderDetails.order.status || '-'}</span>
                            <span className="text-sm font-bold text-[#003545]">{translate('Debiteur')}</span>
                            <span className="text-sm">{orderDetails.order.debtor || '-'}</span>
                            <span className="text-sm font-bold text-[#003545]">{translate('Klantreferentie')}</span>
                            <span className="text-sm">{orderDetails.order.orderReference || '-'}</span>
                        </div>
                    </>)}
                    {orderDetails && (<>
                    <div className="px-4 pt-4 pb-2">
                        <h4 className="text-xl font-bold text-[#003545]">{translate('Ritten')}</h4>
                    </div>
                    {orderDetails.routes?.map((route, index) => (
                        <Collapsible key={index} defaultOpen={index === 0}>
                            <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-3 text-left cursor-pointer">
                                <span className="text-lg font-bold text-[#003545]">
                                    {translate('Rit')} {route.pickup?.address.name || '-'} — {route.delivery?.address.name || '-'}
                                </span>
                                <ChevronDown className="size-4 text-[#003545] shrink-0 transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                                <div className="flex flex-col gap-4 pt-2 pb-4">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4">
                                        {route.pickup && (
                                            <div className="space-y-3">
                                                <h4 className="text-base font-bold text-[#003545] mb-3">{translate('Laadadres')}</h4>
                                                <div>
                                                    <p className="text-sm font-bold text-[#003545]">{translate('Adres')}</p>
                                                    <p className="text-sm">
                                                        {route.pickup.address.name}<br />
                                                        {route.pickup.address.street}<br />
                                                        {route.pickup.address.zipCity}
                                                    </p>
                                                </div>
                                                {route.pickup.timeWindow?.date && (
                                                    <div>
                                                        <p className="text-sm font-bold text-[#003545]">{translate('Tijdvenster')}</p>
                                                        <p className="text-sm">
                                                            {route.pickup.timeWindow.date}, {route.pickup.timeWindow.timeFrom} - {route.pickup.timeWindow.timeTo}
                                                        </p>
                                                    </div>
                                                )}
                                                {orderDetails.order.showPersonalReference && orderDetails.order.personalReferenceLabel && route.pickup.mendrixDebtorReference && (
                                                    <div>
                                                        <p className="text-sm font-bold text-[#003545]">{orderDetails.order.personalReferenceLabel}</p>
                                                        <p className="text-sm">{route.pickup.mendrixDebtorReference}</p>
                                                    </div>
                                                )}
                                                {orderDetails.order.showOurReference && orderDetails.order.ourReferenceLabel && route.pickup.mendrixOurReference && (
                                                    <div>
                                                        <p className="text-sm font-bold text-[#003545]">{orderDetails.order.ourReferenceLabel}</p>
                                                        <p className="text-sm">{route.pickup.mendrixOurReference}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {route.delivery && (
                                            <div className="space-y-3">
                                                <h4 className="text-base font-bold text-[#003545] mb-3">{translate('Losadres')}</h4>
                                                <div>
                                                    <p className="text-sm font-bold text-[#003545]">{translate('Adres')}</p>
                                                    <p className="text-sm">
                                                        {route.delivery.address.name}<br />
                                                        {route.delivery.address.street}<br />
                                                        {route.delivery.address.zipCity}
                                                    </p>
                                                </div>
                                                {route.delivery.timeWindow?.date && (
                                                    <div>
                                                        <p className="text-sm font-bold text-[#003545]">{translate('Tijdvenster')}</p>
                                                        <p className="text-sm">
                                                            {route.delivery.timeWindow.date}, {route.delivery.timeWindow.timeFrom} - {route.delivery.timeWindow.timeTo}
                                                        </p>
                                                    </div>
                                                )}
                                                {orderDetails.order.showPersonalReference && orderDetails.order.personalReferenceLabel && route.delivery.mendrixDebtorReference && (
                                                    <div>
                                                        <p className="text-sm font-bold text-[#003545]">{orderDetails.order.personalReferenceLabel}</p>
                                                        <p className="text-sm">{route.delivery.mendrixDebtorReference}</p>
                                                    </div>
                                                )}
                                                {orderDetails.order.showOurReference && orderDetails.order.ourReferenceLabel && route.delivery.mendrixOurReference && (
                                                    <div>
                                                        <p className="text-sm font-bold text-[#003545]">{orderDetails.order.ourReferenceLabel}</p>
                                                        <p className="text-sm">{route.delivery.mendrixOurReference}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {route.goods?.length > 0 && (
                                        <div className="px-4">
                                            <h4 className="text-base font-bold text-[#003545] mb-3">{translate('Goederen')}</h4>
                                            <div className="space-y-3">
                                                {route.goods.map((good) => (
                                                    <div key={good.id} className="flex items-start justify-between gap-4 rounded-[3px] border border-[#e5e5e5] bg-[#f9f9f9] p-4">
                                                        <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
                                                            <span className="font-bold text-[#003545]">{translate('Type')}</span>
                                                            <span>{good.type || '-'}</span>
                                                            <span className="font-bold text-[#003545]">{translate('Aantal')}</span>
                                                            <span>{good.amount || '-'}</span>
                                                            <span className="font-bold text-[#003545]">{translate('Gewicht')}</span>
                                                            <span>{good.weight ? `${good.weight} kg` : '-'}</span>
                                                            <span className="font-bold text-[#003545]">{translate('Afmetingen')}</span>
                                                            <span>{good.dimensions || '-'}</span>
                                                            <span className="font-bold text-[#003545]">{translate('Barcode')}</span>
                                                            <span>{good.barcode || '-'}</span>
                                                            <span className="font-bold text-[#003545]">{translate('Status')}</span>
                                                            <span>
                                                                {good.scanStatus
                                                                    ? `${translate(good.scanStatus.description)} — ${good.scanStatus.date}`
                                                                    : route.delivery?.actualEnd
                                                                        ? translate('Afgeleverd')
                                                                        : route.pickup?.actualEnd
                                                                            ? translate('Opgehaald')
                                                                            : translate('Order aangemaakt')}
                                                            </span>
                                                            {good.hasTemperatureReporting && good.currentTemperature && (<>
                                                                <span className="font-bold text-[#003545]">{translate('Temperatuur')}</span>
                                                                <span className="flex items-center gap-1">
                                                                    <svg role="img" className="size-4 fill-current text-[#003545]">
                                                                        <use href="#glyph-temperature"></use>
                                                                    </svg>
                                                                    {good.currentTemperature} ℃
                                                                </span>
                                                            </>)}
                                                        </div>
                                                        <GoodTemperatureActions
                                                            good={good}
                                                            traceUuid={route.delivery?.traceUuid}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {route.delivery?.traceUuid && (
                                        <div className="px-4">
                                            <Button variant="primary" asChild className="w-full">
                                                <a href={trackAndTraceUrl(route.delivery.traceUuid, { goodId: route.goods?.[0]?.id, fromOrderId: selectedOrderId })} target="_blank" rel="noopener noreferrer">
                                                    <ExternalLink className="size-4" />
                                                    {translate('Track & Trace')}
                                                </a>
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CollapsibleContent>
                            <div className="px-4"><Separator /></div>
                        </Collapsible>
                    ))}
                    </>)}
                </>)}
                </div>
                {orderDetails && (
                    <SheetFooter className="pt-2">
                        {orderDetails.order.externalOrderId && (
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button variant="primary" asChild className="flex-1">
                                    <a href={`/order/${orderDetails.order.externalOrderId}/vrachtbrief`}>
                                        {translate('Download vrachtbrief')}
                                    </a>
                                </Button>
                                <Button variant="primary" asChild className="flex-1">
                                    <a href={`/order/${orderDetails.order.externalOrderId}/etiket`}>
                                        {translate('Download etiket')}
                                    </a>
                                </Button>
                            </div>
                        )}
                        <Button variant="primary-outline" asChild className="w-full">
                            <a href={`/orders/${selectedOrderId}/detail`}>
                                {translate('Bekijk volledige orderdetails')}
                            </a>
                        </Button>
                    </SheetFooter>
                )}
            </SheetContent>
        </Sheet>
    );
}
